import { DecimalPipe } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { CustomNumberFormatPipe, EThemeName, ITimesTemp, ThemeService } from '@wind-log_library/fe-component-library';
import { CS_LINE_CHART_OPTIONS } from 'app/shared/helper/highchart-options';
import { ONE_DAY_IN_MILLISECONDS } from 'app/shared/helper/time.contants';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import Highcharts, { SeriesOptionsType } from 'highcharts';
import moment from 'moment';
import { isNil } from 'lodash';
import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { DateTimeFormatService } from 'app/shared/services/date-time-format/date-time-format.service';
import { getXDateFormat } from 'app/shared/helper/highcharts.helper';
import { getUnitLabel, getUnitSuffix, isPercentUnit, toUnitValue } from 'app/shared/helper/kpi-units.helper';

@Component({
  selector: 'app-kpi-availability-daily-chart',
  templateUrl: './kpi-availability-daily-chart.component.html',
  styleUrls: ['./kpi-availability-daily-chart.component.scss'],
  standalone: false,
})
export class KpiAvailabilityDailyChartComponent implements OnInit, OnChanges {
  data = [];
  rawData = [];
  parks = [];
  public colorScheme: any;
  customAvailabilities = [];
  chartId = Math.random().toString(36).substring(2);
  kpiTarget: number;
  from;
  to;
  yScaleMin = 90;
  yScaleMax = 0;
  public lastYear;
  public tooltipData;
  public units: string;

  @Input() availabilityService;
  @Input() availabilitySelected;
  @Input() availabilityData;
  @Input() loading;
  @Input() assetId: string;
  @Input() timesTamp: ITimesTemp;

  @ViewChild('tooltipTemplate', { static: false }) tooltipTemplate: ElementRef<HTMLDivElement>;
  myAvailabilitySelected;
  public theme$: Observable<EThemeName>;

  public chartOptions: Highcharts.Options;
  public chartLabels: number[] = [];

  public highcharts = Highcharts;
  public chartRef: Highcharts.Chart | null = null;

  private unsubscriber = new ReplaySubject();

  constructor(
    private themeService: ThemeService,
    private translateService: TranslateService,
    private customNumberFormatPipe: CustomNumberFormatPipe,
    private decimalPipe: DecimalPipe,
    private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
    private dateTimeFormatService: DateTimeFormatService,
  ) {}

  ngOnInit() {
    this.subscribeToLanguageChange();
    this.setColorSchemeStacked();
    this.tooltipData = {
      name: '',
      value: '',
      target: '',
    };
  }

  public chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chartRef = chart;
  };

  private getValue = (value, unit = ''): string => {
    if (isNil(value)) return '-';
    return this.customNumberFormatPipe.transform(this.decimalPipe.transform(value, '1.2-2')) + unit;
  };

  public setChartOptions(): void {
    if (!this.colorScheme) return;
    if (!this.data?.[0]?.series) {
      this.chartOptions = Highcharts.merge(CS_LINE_CHART_OPTIONS, {
        title: { text: '' },
        legend: { enabled: false },
        exporting: { enabled: false },
        xAxis: {
          min: this.from,
          max: this.to,
        },
        yAxis: {
          min: 0,
          max: 100,
          title: { text: '%' },
        },
        series: [],
      });
      return;
    }
    this.chartLabels = this.getChartLabels();
    const dateTimeFormatPreferencePipe = this.dateTimeFormatPreferencePipe;
    const targetLabel = this.translateService.instant('COMMON.TARGET');
    const selectAvailability = this.myAvailabilitySelected?.label;
    const target = this.data[0]?.series[0]?.kpiTarget || 0;
    const getValueRef = this.getValue;
    const unitSuffix = getUnitSuffix(this.units);
    const series: SeriesOptionsType[] = [
      {
        type: 'area',
        id: 'actual',
        name: 'test',
        data: this.data[0].series.map(d => [d.name, d.value]),
        color: this.colorScheme.domain[0] + 'd0',
        lineColor: this.colorScheme.domain[0],
        lineWidth: 1.5,
        marker: { enabled: false, symbol: 'circle', radius: 3 },
      },
      {
        type: 'line',
        id: 'target',
        name: 'target',
        data: this.data[0].series.map(d => [d.name, target]),
        color: this.themeService.getColor('--budget-follow-up-budget'),
        lineWidth: 1.5,
        marker: { enabled: false, symbol: 'circle', radius: 3 },
      },
    ];
    const xAxis = Highcharts.merge(CS_LINE_CHART_OPTIONS.xAxis, {
      min: this.from,
      max: this.to,
      dateTimeLabelFormats: {
        day: getXDateFormat('dm', this.dateTimeFormatService),
        hour: getXDateFormat('t', this.dateTimeFormatService),
        minute: getXDateFormat('t', this.dateTimeFormatService),
      },
    });
    const yAxis = Highcharts.merge(CS_LINE_CHART_OPTIONS.yAxis, {
      title: {
        text: getUnitLabel(this.units),
      },
      min: 0,
      max: isPercentUnit(this.units) ? 100 : undefined,
    });
    this.chartOptions = Highcharts.merge(CS_LINE_CHART_OPTIONS, {
      chart: {
        height: 275,
        animation: { duration: 0 },
      },
      xAxis: xAxis,
      yAxis: yAxis,
      tooltip: {
        shared: true,
        hideDelay: 0,
        animation: { duration: 0 },
        formatter: function () {
          const data = this.points.find(point => point.series.userOptions.id === 'actual')?.y;
          const target = this.points.find(point => point.series.userOptions.id === 'target')?.y;
          return `
            <div class="font-weight-bold" style="font-size: 16px; font-weight: 600;">${dateTimeFormatPreferencePipe.transform(moment(this.x).toDate(), 'dmy')}</div><br/><br/>
            ${selectAvailability}: <b>${getValueRef(data, unitSuffix)}</b><br/>
            ${targetLabel}: <b>${getValueRef(target, unitSuffix)}</b><br/>
          `;
        },
      },
      title: { text: '' },
      legend: { enabled: false },
      exporting: { enabled: false },
      plotOptions: {
        area: { animation: { duration: 0 } },
        line: { animation: { duration: 0 } },
      },
      series: series,
    });
  }

  public getChartLabels(): number[] {
    const xAxisLabels = [];
    if (this.from && this.to) {
      for (let x = this.from; x < this.to; x += ONE_DAY_IN_MILLISECONDS) {
        xAxisLabels.push(x);
      }
    }
    return xAxisLabels;
  }

  public getLongDateFormat(date): string {
    return date ? this.dateTimeFormatPreferencePipe.transform(moment(date).toDate(), 'dmy') : '';
  }

  private prepareData(): void {
    this.data = [
      {
        name: '',
        series: [],
      },
    ];
    this.yScaleMin = 90;
    this.yScaleMax = 0;
    this.units = undefined;

    if (!this.rawData || !this.from || !this.to) {
      this.setChartOptions();
      return;
    }

    if (this.myAvailabilitySelected && this.myAvailabilitySelected !== '' && this.myAvailabilitySelected.index !== -1) {
      const index = this.myAvailabilitySelected?.kpiIndex ?? this.myAvailabilitySelected?.index;
      this.rawData
        .filter(data => data.date >= this.from && data.date <= this.to)
        .forEach(d => {
          const availability = d.availabilities.find(av => av.kpiIndex === index);
          if (availability) {
            this.units = availability.units;
          }
          this.data[0].series.push({
            name: d.date,
            value: toUnitValue(availability?.rangeResult, availability?.units) || 0,
            kpiTarget: availability?.target == null ? -1 : toUnitValue(availability.target, availability.units),
          });
        });

      if (this.rawData && this.rawData.length > 0) {
        this.loading = false;
      }

      this.data[0].series.forEach(data => {
        const max = Math.max(data.kpiTarget, data.value);
        const min = Math.min(data.kpiTarget, data.value);
        this.yScaleMax = Math.max(this.yScaleMax, max);
        this.yScaleMin = Math.min(this.yScaleMin, min);
      });
    }

    this.setChartOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    let shouldPrepareData = false;

    if (changes.availabilityData && changes.availabilityData.currentValue) {
      this.rawData = changes.availabilityData.currentValue;
      shouldPrepareData = true;
    }
    if (
      changes.availabilitySelected &&
      (changes.availabilitySelected.currentValue || changes.availabilitySelected.currentValue === 0)
    ) {
      this.myAvailabilitySelected = changes.availabilitySelected.currentValue;
      shouldPrepareData = true;
    }
    if (changes.timesTamp && changes.timesTamp.currentValue) {
      this.from = changes.timesTamp.currentValue.startTS;
      this.to = changes.timesTamp.currentValue.endTS;
      shouldPrepareData = true;
    }

    if (shouldPrepareData) {
      this.prepareData();
    }
  }

  private setColorSchemeStacked(): void {
    this.colorScheme = {
      domain: [this.themeService.getColor('--primary-background')],
      ticks: this.themeService.getColor('--font'),
    };
    this.theme$ = this.themeService.getTheme().pipe(
      tap(() => {
        this.colorScheme = {
          domain: [this.themeService.getColor('--primary-background')],
          ticks: this.themeService.getColor('--font'),
        };
        this.setChartOptions();
      }),
    );
  }

  private subscribeToLanguageChange(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe((event: LangChangeEvent) => {
      this.setChartOptions();
    });
  }
}
